import UrlManager from '@readme/server-shared/UrlManager'; // eslint-disable-line readme-internal/no-restricted-imports
import { VariablesContext, SelectedAppContext } from '@readme/variable';
import PropTypes from 'prop-types';
import React, { createContext, useMemo, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SWRConfig } from 'swr';

import LabsContext from '@core/utils/Labs/context';

import ProjectState, { ProjectContext } from './project';

const BaseUrlContext = createContext('/');
BaseUrlContext.displayName = 'BaseUrlContext';

const GlossaryContext = createContext([]);
GlossaryContext.displayName = 'GlossaryContext';

// TODO: Move this into each respective @readme/* repo instead. The exported
// context objects should have their display names already set.
SelectedAppContext.displayName = 'SelectedAppContext';
VariablesContext.displayName = 'VariablesContext';

const defaultUserContext = {
  user: false,
  permissions: [],
  mutateUser: () => {},
};
const UserContext = createContext(defaultUserContext);
UserContext.displayName = 'UserContext';

/**
 * @deprecated
 * @see useLegacyCompatibleBranchName
 */
const VersionContext = createContext({ version: '' });
VersionContext.displayName = 'VersionContext';

const APIBaseUrlContext = createContext('/');
APIBaseUrlContext.displayName = 'APIBaseUrlContext';

const LocalizationContext = createContext('en');
LocalizationContext.displayName = 'LocalizationContext';

const EnterpriseParentContext = createContext({
  /** On the Hub, this will only be true on the GLP. */
  isParent: false,
  /** Does the group have `singleProjectEnterprise` on? */
  singleParent: false,
  /** Global Landing Page HTML markup. */
  lpMarkup: '',
});
EnterpriseParentContext.displayName = 'EnterpriseParentContext';

const defaultAppMetaContext = {
  baseUrl: '/',
  robots: '',
  metaTitle: '',
  title: '',
  type: '',
  keywords: '',
};

const AppMetaContext = createContext({
  ...defaultAppMetaContext,
  updateAppMeta: () => {},
});
AppMetaContext.displayName = 'AppMetaContext';

/**
 * @see `config.get('client.configAllowList')`
 */
const ConfigContext = createContext({});
ConfigContext.displayName = 'ConfigContext';

/**
 * Global SWR fetcher that normalizes requests to our API endpoints. It always
 * attempts to resolve successful responses with JSON data. Otherwise, it
 * returns a rejected Promise with an Error object that contains some metadata
 * about the failed request.
 * @param {string} url
 * @return {Promise}
 */
async function fetcher(url) {
  const response = await fetch(url);

  // Reject the fetch with a general error object if the response was
  // unsuccessful with any status code outside the range of 200-299.
  if (!response.ok) {
    const error = new Error(`An error occurred while fetching "${url}"`);
    error.status = response.status;
    error.statusText = response.statusText;
    error.url = url;
    throw error;
  }

  return response.json();
}

export const makeEnterpriseParentState = (project, lpMarkup, isDetachedProductionSite) => {
  const isSPEGroup = !!project?.flags?.singleProjectEnterprise;
  const groupChildren = project?.childrenProjects || [];
  const stagingEnv = !project.flags?.staging ? 'off' : !isDetachedProductionSite ? 'stage' : 'prod';
  return {
    children: groupChildren,
    isParent: groupChildren.length || isSPEGroup,
    lpMarkup,
    singleParent: isSPEGroup,
    stagingEnv,
  };
};

const AppContext = ({
  apiBaseUrl,
  baseUrl,
  config,
  context,
  children,
  enterpriseLP,
  isDetachedProductionSite,
  language,
  name,
  meta: propMeta = {},
}) => {
  const [selected, changeSelected] = useState('');
  const { project, variables, terms, version, labs } = context;
  const location = useLocation();
  const isSuggestedEditRoute = /^\/edit|suggested-edits/.test(location.pathname);

  const [user, setUser] = useState({
    ...context.user,
    mutateUser: next =>
      setUser(state => {
        next.mutateUser = state.mutateUser;
        if (JSON.stringify(state) !== JSON.stringify(next)) return next;
        return state;
      }),
  });

  const [meta, setMeta] = useState({
    ...defaultAppMetaContext,
    ...propMeta,
    updateAppMeta: nextState =>
      setMeta(state => {
        nextState.updateAppMeta = state.updateAppMeta;
        if (JSON.stringify(state) !== JSON.stringify(nextState)) return nextState;
        return state;
      }),
  });

  // Extend the config object with the app name
  // Useful to know which app is currently running for components that are shared between apps
  const configValue = useMemo(() => {
    return {
      ...config,
      name,
    };
  }, [config, name]);

  /**
   * Indicates whether this particular route should be visible to search index
   * crawlers or not. Prevents indexing all suggested edit routes. Otherwise,
   * disable indexing on pages that are "hidden" or has explicitly turned off
   * indexing in their SEO metadata.
   */
  const disableCrawlers = !!meta.hidden || meta.robots === 'noindex' || isSuggestedEditRoute;
  const [enterpriseParent] = useState(makeEnterpriseParentState(project, enterpriseLP, isDetachedProductionSite));

  /**
   * This <title> value should match the same logic as the <title> tag in
   * server-side rendering otherwise Helmet can override things incorrectly.
   *
   * @see packages/hub/views/includes/head.jade
   */
  const titleTag =
    project.seo?.overwrite_title_tag && meta.metaTitle ? meta.metaTitle : meta.title || project.name || 'Loading…';

  // Info for meta tags
  const description = meta.description ?? project.description;
  const title = meta.metaTitle || meta.title || project.name;

  // Create the og-image URL. Mirroring the logic in packages/hub/views/includes/head.jade
  // The og logic in the jade file is for the initial SSR, this function will overwrite the tags in SPA navigation events
  const getOgImageUrl = () => {
    if (meta.image && meta.image[0]) {
      return meta.image[0];
    }

    if (
      project?.name &&
      meta?.title &&
      ['docs', 'reference', 'tutorials', 'changelog', 'discuss', 'home'].includes(meta.type)
    ) {
      // checks the color scheme, if the scheme is system, we default to light
      // if the scheme is dark, we use the dark logo (logo.dark_mode)
      const variant = project.appearance?.colorScheme === 'dark' ? 'dark' : 'light';
      const logoUrl =
        variant === 'dark'
          ? project.appearance?.logo?.dark_mode?.[0] ||
            project.appearance?.logo_white?.[0] ||
            project.appearance?.logo?.[0] // fallback to the light logo if the dark logo is not set
          : project.appearance?.logo?.[0] || '';

      const imageType = meta.type;
      const imageTitle = meta.metaTitle || meta.title || '';
      const projectTitle = project.name.slice(0, 25);
      const imageDescription = meta.type === 'home' ? project.description || '' : meta.description || '';
      const mainColor = project.appearance?.colors?.main || '#018ef5';

      const ogImageParams = new URLSearchParams({
        type: imageType,
        title: imageTitle,
        projectTitle,
        description: imageDescription,
        logoUrl,
        color: mainColor,
        variant,
      })
        .toString()
        .replace(/\+/g, '%20');

      return `${config.asset_url}/og-image/create?${ogImageParams}`;
    }

    return null;
  };

  const ogImageUrl = getOgImageUrl();

  return (
    <HelmetProvider>
      <ConfigContext.Provider value={configValue}>
        <BaseUrlContext.Provider value={baseUrl}>
          <APIBaseUrlContext.Provider value={apiBaseUrl}>
            <LocalizationContext.Provider value={language}>
              <VariablesContext.Provider value={variables}>
                <GlossaryContext.Provider value={terms}>
                  <ProjectState value={project}>
                    <VersionContext.Provider value={version}>
                      <UserContext.Provider value={user}>
                        <AppMetaContext.Provider value={meta}>
                          <SelectedAppContext.Provider value={{ selected, changeSelected }}>
                            <EnterpriseParentContext.Provider value={enterpriseParent}>
                              <LabsContext.Provider value={labs}>
                                <Helmet>
                                  <title>{titleTag}</title>
                                  <meta content={description} name="description" />
                                  <meta content={title} property="og:title" />
                                  <meta content={description} property="og:description" />
                                  <meta content={ogImageUrl || ''} property="og:image" />
                                  <meta content={ogImageUrl || ''} name="twitter:image" />
                                  <meta content="1200" property="og:image:width" />
                                  <meta content="630" property="og:image:height" />
                                  <meta content={title} property="twitter:title" />
                                  <meta content={description} property="twitter:description" />
                                  <meta content={meta.keywords} property="keywords" />
                                  {/* Okay I don't love this, because `index` isn't a real thing.
                              But we can't conditionally show this tag using MetaTags, because
                              MetaTags won't remove it from the DOM on page change if it's
                              removed here. So this is the best option I could find. */}
                                  <meta content={disableCrawlers ? 'noindex' : ''} name="robots" />
                                  <meta content={title} name="twitter:title" />
                                  <meta content={description} name="twitter:description" />
                                  {!!meta.keywords && <meta content={meta.keywords} property="keywords" />}
                                  {!!disableCrawlers && <meta content="noindex" name="robots" />}
                                </Helmet>
                                <SWRConfig value={{ fetcher }}>{children}</SWRConfig>
                              </LabsContext.Provider>
                            </EnterpriseParentContext.Provider>
                          </SelectedAppContext.Provider>
                        </AppMetaContext.Provider>
                      </UserContext.Provider>
                    </VersionContext.Provider>
                  </ProjectState>
                </GlossaryContext.Provider>
              </VariablesContext.Provider>
            </LocalizationContext.Provider>
          </APIBaseUrlContext.Provider>
        </BaseUrlContext.Provider>
      </ConfigContext.Provider>
    </HelmetProvider>
  );
};

AppContext.propTypes = {
  apiBaseUrl: PropTypes.string,
  baseUrl: PropTypes.string,
  children: PropTypes.any,
  config: PropTypes.object,
  context: PropTypes.object,
  enterpriseLP: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  // Ref propType: https://stackoverflow.com/a/51127130
  firstRun: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.bool })]),
  isDetachedProductionSite: PropTypes.bool,
  language: PropTypes.string,
  meta: PropTypes.object,
  // App name (i.e. wherein AppContext is being used)
  name: PropTypes.oneOf(['Dash', 'Hub', 'Micro', 'Playground']),
  rdmd: PropTypes.object,
};

export * from './types';
export default AppContext;
export {
  APIBaseUrlContext,
  AppMetaContext,
  BaseUrlContext,
  ConfigContext,
  EnterpriseParentContext,
  GlossaryContext,
  LocalizationContext,
  ProjectContext,
  UrlManager,
  UserContext,
  VersionContext,
  VariablesContext,
};
